import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Copy, Check, Loader2, UserPlus, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTrips } from "../../context/TripsContext";
import { useAuth } from "../../context/AuthContext";
import { tripInvitesService } from "../../services/supabase";
import { CurrencySelect } from "../../components/CurrencySelect";

export const SettingsPage = (): React.ReactNode => {
  const navigate = useNavigate();
  const { currentTrip, updateTrip } = useTrips();
  const { firebaseUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [currency, setCurrency] = useState(currentTrip?.currency || 'USD');
  const [isSavingCurrency, setIsSavingCurrency] = useState(false);

  // Update currency when currentTrip changes
  useEffect(() => {
    if (currentTrip?.currency) {
      setCurrency(currentTrip.currency);
    }
  }, [currentTrip?.currency]);

  const handleCopyInviteLink = async () => {
    if (!currentTrip || !firebaseUser) return;

    setLoading(true);
    try {
      const invite = await tripInvitesService.createInvite({
        trip_id: currentTrip.id,
        invited_by: firebaseUser.uid,
      });

      const link = tripInvitesService.getInviteLink(invite.invite_code);

      // Copy to clipboard
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error generating invite link:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewInvite = async () => {
    if (!currentTrip || !firebaseUser) return;

    setPreviewLoading(true);
    try {
      const invite = await tripInvitesService.createInvite({
        trip_id: currentTrip.id,
        invited_by: firebaseUser.uid,
      });

      // Navigate to invite page with demo parameter
      navigate(`/invite/${invite.invite_code}?demo=true`);
    } catch (error) {
      console.error('Error generating invite for preview:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSaveCurrency = async () => {
    if (!currentTrip || currency === currentTrip.currency) return;

    setIsSavingCurrency(true);
    try {
      await updateTrip(currentTrip.id, { currency });
    } catch (error) {
      console.error('Error updating trip currency:', error);
    } finally {
      setIsSavingCurrency(false);
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <Card>
        <CardHeader>
          <CardTitle>Trip Settings</CardTitle>
          <CardDescription>Manage your trip preferences and participants</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Participants Section */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Participants</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Invite others to collaborate on this trip
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyInviteLink}
                  disabled={loading || !currentTrip}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : copied ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Link Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2 h-4 w-4" />
                      Copy Link
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviewInvite}
                  disabled={previewLoading || !currentTrip}
                  className="px-3"
                >
                  {previewLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
                <Button variant="outline" size="sm" disabled>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Invite to Trip
                </Button>
              </div>
            </div>

            {/* Trip Currency Section */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Trip Currency</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Set the primary currency for this trip
              </p>
              <div className="flex items-end gap-2">
                <div className="flex-1 max-w-xs">
                  <Label htmlFor="currency" className="mb-2 block">Currency</Label>
                  <CurrencySelect
                    value={currency}
                    onValueChange={setCurrency}
                  />
                </div>
                <Button
                  onClick={handleSaveCurrency}
                  disabled={isSavingCurrency || currency === currentTrip?.currency}
                  size="sm"
                >
                  {isSavingCurrency ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </div>

            {/* Budget Settings */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Budget Settings</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Enable or disable budget tracking for this trip
              </p>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="use-budget"
                  checked={currentTrip?.use_budget || false}
                  onChange={async (e) => {
                    if (!currentTrip) return;
                    await updateTrip(currentTrip.id, { use_budget: e.target.checked });
                  }}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="use-budget" className="cursor-pointer">
                  Enable budget tracking
                </Label>
              </div>
            </div>

            {/* Payment Method Settings */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Payment Method Settings</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Choose which payment methods are available for expenses
              </p>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="only-cooperate-card"
                  checked={currentTrip?.only_cooperate_card ?? true}
                  onChange={async (e) => {
                    if (!currentTrip) return;
                    await updateTrip(currentTrip.id, { only_cooperate_card: e.target.checked });
                  }}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="only-cooperate-card" className="cursor-pointer">
                  {currentTrip?.only_cooperate_card ?? true ? 'Only Cooperate card' : 'Personal and Cooperate card'}
                </Label>
              </div>
            </div>

            {/* Per Diem Settings */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Per Diem Settings</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Configure how per diem values are calculated
              </p>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="round-up-per-diem"
                  checked={currentTrip?.round_up_per_diem || false}
                  onChange={async (e) => {
                    if (!currentTrip) return;
                    try {
                      await updateTrip(currentTrip.id, { round_up_per_diem: e.target.checked });
                    } catch (error) {
                      console.error('Error updating round_up_per_diem:', error);
                      alert(`Failed to update setting: ${error instanceof Error ? error.message : 'Unknown error'}`);
                    }
                  }}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="round-up-per-diem" className="cursor-pointer">
                  Round up per diem values
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
